/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * PER-PARTY OpenSanctions ROWS (v1_645).
 *
 * The analysis-time OpenSanctions sweep (api/df-process.js earlyPartiesScreen)
 * screens every named director and UBO the moment they are known. Its per-person
 * results are persisted to collected_data.partyScreenings so the OpenSanctions
 * panel, the audit record and the decision record can each show one row per
 * person ALONGSIDE the company row — an independent OpenSanctions record, never
 * a replacement for the identity-verification step, which is a separate control.
 *
 * This module is the single place that turns the board's RESOLVED verification
 * parties plus the stored screening results into display rows, so the panel and
 * every report generator agree. It reads the resolved parties (natural-person
 * directors, UBOs and authorised signatories of the applicant — v1_650), NOT the
 * raw directors[]/ubos[] arrays: a document-sourced non-party or a corporate
 * entity is context on the board, never a screened individual, so it must never
 * appear as an OpenSanctions row (Sage Capital, 16/09/26). A resolved party not
 * yet screened is still shown honestly as such. Pure, no I/O; the client bundle
 * and any server generator import it.
 */
import { resolveVerificationParties } from './verification-parties.js';

// Status vocabulary. 'clear' / 'hit' / 'caution' come from a completed screen;
// 'not_screened' is an extracted party the sweep has not (yet) covered.
export const PARTY_STATUS_LABEL = Object.freeze({
  clear: 'Clear, no matches',
  hit: 'Potential match, review',
  caution: 'Partial match, review',
  not_screened: 'Extracted, not yet screened',
  // v1_816: an officer's resolution of a potential or partial match.
  resolved: 'Potential match resolved: not the same person',
  confirmed: 'Confirmed match: escalated',
});

// v1_816: THE OFFICER'S RESOLUTION OF A SCREENING MATCH. Stored in
// collected_data.screeningResolutions: { id, kind ('party' | 'intermediary' |
// 'entity'), name, outcome ('not_same_person' | 'confirmed_match'), reason,
// matchIds, by, at }. A resolution applies to a screen result when every match
// the result carries was among the matches the officer resolved (so a screen
// run again that returns the same records stays resolved, and one that
// returns a NEW record is open again). A result stored before match detail
// existed (no ids) is covered when it is no newer than the resolution. The
// latest applicable resolution stands; a confirmed match is never undone by a
// later "not the same person" on the same records, only by a new screen.
export function resolutionFor(kind, name, rec, resolutions = []) {
  const k = normName(name);
  if (!k || !rec || !['hit', 'caution'].includes(rec.status)) return null;
  const ids = Array.isArray(rec.matches) ? rec.matches.map(m => m && m.id).filter(Boolean) : [];
  const recAt = rec.checkedAt ? new Date(rec.checkedAt).getTime() : 0;
  const applicable = (Array.isArray(resolutions) ? resolutions : []).filter(r => {
    if (!r || (r.kind || 'party') !== kind || normName(r.name) !== k) return false;
    if (!['not_same_person', 'confirmed_match'].includes(r.outcome)) return false;
    const rIds = Array.isArray(r.matchIds) ? r.matchIds : [];
    if (ids.length) return ids.every(id => rIds.includes(id));
    return !recAt || new Date(r.at).getTime() >= recAt;
  });
  if (!applicable.length) return null;
  const confirmed = applicable.filter(r => r.outcome === 'confirmed_match');
  const pick = (arr) => arr.slice().sort((a, b) => new Date(b.at) - new Date(a.at))[0];
  return confirmed.length ? pick(confirmed) : pick(applicable);
}

// The match detail a row shows: matched name, lists or datasets, score, date
// of birth and nationality where the list gives them, and the record's link.
export function matchLines(rec) {
  return (Array.isArray(rec && rec.matches) ? rec.matches : []).map(m => ({
    id: m.id || null,
    name: m.caption || m.name || '',
    score: m.score != null ? Number(m.score) : null,
    datasets: Array.isArray(m.datasets) ? m.datasets : [],
    topics: Array.isArray(m.topics) ? m.topics : [],
    birthDate: Array.isArray(m.birthDate) ? m.birthDate : (m.birthDate ? [m.birthDate] : []),
    nationality: Array.isArray(m.nationality) ? m.nationality : (m.nationality ? [m.nationality] : []),
    url: m.url || (m.id ? `https://www.opensanctions.org/entities/${encodeURIComponent(m.id)}/` : null),
  }));
}

function normName(n) {
  return String(n || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '');
}

/**
 * Build one row per extracted natural party (director / UBO), merging in the
 * stored OpenSanctions result where one exists.
 *
 * @param report          the decision board (resolved via resolveVerificationParties)
 * @param partyScreenings collected_data.partyScreenings (array of stored results)
 * @returns [{ name, role, status, checkedAt, source, matchCount, topics }]
 */
export function partyScreeningRows(report = {}, partyScreenings = [], resolutions = []) {
  const b = report && typeof report === 'object' ? report : {};
  // The party set is the RESOLVED verification parties (deduped natural-person
  // directors/UBOs/signatories of the applicant), not the raw arrays. A UBO-only
  // person reads as "Beneficial owner"; a director reads "Director".
  const parties = resolveVerificationParties(b).map(p => ({
    name: p.name,
    alsoListedAs: Array.isArray(p.alsoListedAs) ? p.alsoListedAs : [],
    role: /^Client \(natural person\)/.test(p.role) ? 'Client'
      : /^UBO\b/.test(p.role) ? 'Beneficial owner'
      : /^Authorised signatory\b/.test(p.role) ? 'Authorised signatory'
      : 'Director',
  }));

  // Stored screening results, indexed by name for exact and containment lookup
  // (a result stored under a shorter spelling still matches its resolved party).
  const stored = (Array.isArray(partyScreenings) ? partyScreenings : []).filter(s => s && s.name);
  const resultFor = (name) => {
    const k = normName(name);
    if (!k) return null;
    let hit = stored.find(s => normName(s.name) === k);
    if (!hit) hit = stored.find(s => { const sk = normName(s.name); return sk && (sk.includes(k) || k.includes(sk)); });
    return hit || null;
  };

  const rows = [];
  const seen = new Set();
  // v1_888: a record's own status after any officer resolution of THAT record
  // (a resolution names the form it was made under; it never covers another).
  const recStatus = (rec) => {
    const raw = (rec && ['clear', 'hit', 'caution'].includes(rec.status)) ? rec.status : 'not_screened';
    const res = rec ? resolutionFor('party', rec.name, rec, resolutions) : null;
    return { raw, res, status: res ? (res.outcome === 'confirmed_match' ? 'confirmed' : 'resolved') : raw };
  };
  const RANK = { confirmed: 6, hit: 5, caution: 4, not_screened: 3, resolved: 2, clear: 1 };
  for (const { name, role, alsoListedAs } of parties) {
    const k = normName(name);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    const primary = resultFor(name);
    // v1_888: records stored under the person's other name forms (an officer's
    // merge, or the row merge's alsoListedAs) are this person's too: shown on
    // the one card and counted, never hidden and forgotten.
    const extra = [];
    for (const a of (alsoListedAs || [])) {
      const ak = normName(a);
      if (!ak || seen.has(ak)) continue;
      seen.add(ak);
      const r = stored.find(x => normName(x.name) === ak && x !== primary && !extra.includes(x));
      if (r) extra.push(r);
    }
    const recs = [primary, ...extra].filter(Boolean);
    const states = recs.map(r => ({ rec: r, ...recStatus(r) }));
    // The lead record is the one most in need of the officer; with one record
    // the card reads exactly as before.
    const lead = states.slice().sort((x, y) => RANK[y.status] - RANK[x.status])[0] || null;
    const s = lead ? lead.rec : null;
    const rawStatus = lead ? lead.raw : 'not_screened';
    const resolution = lead ? lead.res : null;
    let status = lead ? lead.status : 'not_screened';
    // An unscreened primary keeps the person not screened unless another form is open.
    if (!primary && !['confirmed', 'hit', 'caution'].includes(status)) status = 'not_screened';
    rows.push({
      rawStatus,
      resolution: resolution ? { outcome: resolution.outcome, reason: resolution.reason || '', by: resolution.by || '', at: resolution.at || null } : null,
      matches: s ? matchLines(s) : [],
      // v1_888: every record for this person, one per name form screened.
      records: states.map(x => ({ screenedAs: x.rec.name, rawStatus: x.raw, status: x.status, matches: matchLines(x.rec), checkedAt: x.rec.checkedAt || x.rec.timestamp || null, resolution: x.res ? { outcome: x.res.outcome, reason: x.res.reason || '', by: x.res.by || '', at: x.res.at || null } : null, error: (x.raw === 'not_screened' && x.rec.error) || '' })),
      name,
      role: role || 'Party',
      status,
      checkedAt: (s && (s.checkedAt || s.timestamp)) || null,
      source: (s && s.source) || 'OpenSanctions',
      matchCount: (s && Number(s.matchCount)) || 0,
      topics: (s && s.topics) || '',
      // A stored not-screened record can carry why it could not run (no key, or
      // a failed call), so the panel shows the reason rather than a bare gap.
      error: (s && status === 'not_screened' && s.error) || '',
    });
  }
  return rows;
}

// PURE BOARD-ROW STATE for the Sanctions and PEP check (v1_741). The row reads
// the company entity screen AND every resolved party together: it is clear and
// done ONLY when the company is clear (or clear with dismissed notes) AND every
// resolved party carries a clear result. An unscreened party, or any individual
// match, keeps the row open and not done, so the header can never read clear
// over an individual who has no result. Pure, so both the dashboard and the
// harness read the same logic.
export function sanctionsRowState(entitySanctions, partyRows = [], opts = {}) {
  // Individual onboarding (21/10/2026): an individual's file is the client's
  // name screen and the client screened as a person. The row is never done
  // without the person's own result, and its wording names the client.
  const individual = !!(opts && opts.individual);
  const lead = individual ? 'Name screen clear' : 'Company clear';
  const companyStatus = entitySanctions ? entitySanctions.status : null;
  const companyClear = companyStatus === 'clear' || companyStatus === 'clear_with_notes';
  const rows = Array.isArray(partyRows) ? partyRows : [];
  const unscreened = rows.filter(r => r && r.status === 'not_screened');
  // v1_816: a confirmed match is always a blocking hit; a resolved row reads clear.
  const confirmed = rows.filter(r => r && r.status === 'confirmed');
  const indHits = rows.filter(r => r && r.status === 'hit');
  const indCautions = rows.filter(r => r && r.status === 'caution');

  // Individual onboarding (walk wi59deeb70): an individual's screen is the
  // client screened as a person. The name screen an organisation gets is not
  // run on the interview path, so its absence does not hold the check; a name
  // screen that was run and is not clear still does (below).
  if (!entitySanctions && individual) {
    if (!rows.length) return { status: 'partial', detail: 'The client has not been screened as a person', done: false };
    if (confirmed.length) return { status: 'hit', detail: 'Client confirmed match: escalated, approval blocked', done: false, confirmed: confirmed.length };
    if (indHits.length) return { status: 'hit', detail: 'Client sanctions or PEP match: review required', done: false };
    if (indCautions.length) return { status: 'caution', detail: 'Client partial match: review required', done: false };
    if (unscreened.length) return { status: 'partial', detail: 'The client has not been screened as a person', done: false };
    const res0 = rows.filter(r => r && r.status === 'resolved').length;
    return { status: 'clear', detail: res0 ? `Clear (client; ${res0} potential match${res0 === 1 ? '' : 'es'} resolved as not the same person)` : 'Clear, no matches (client)', done: true, ...(res0 ? { resolved: res0 } : {}) };
  }
  if (!entitySanctions) return { status: 'none', detail: 'Not yet screened', done: false };
  if (companyStatus === 'running') return { status: 'running', detail: 'Screening...', done: false };
  if (companyStatus === 'no_key') return { status: 'no_key', detail: 'API key required', done: false };
  if (!companyClear) {
    if (companyStatus === 'hit') return { status: 'hit', detail: 'Sanctions match: review required', done: false };
    if (companyStatus === 'caution') return { status: 'caution', detail: 'Partial matches: review required', done: false };
    return { status: 'error', detail: 'Screening error', done: false };
  }
  // Company is clear: fold in the individuals.
  if (confirmed.length) return { status: 'hit', detail: `${lead}, ${confirmed.length} individual confirmed match${confirmed.length === 1 ? '' : 'es'}: escalated, approval blocked`, done: false, confirmed: confirmed.length };
  if (indHits.length) return { status: 'hit', detail: `${lead}, ${indHits.length} individual sanctions match${indHits.length === 1 ? '' : 'es'}: review required`, done: false };
  if (indCautions.length) return { status: 'caution', detail: `${lead}, ${indCautions.length} individual partial match${indCautions.length === 1 ? '' : 'es'}: review required`, done: false };
  if (unscreened.length) return { status: 'partial', detail: `${lead}, ${unscreened.length} individual${unscreened.length === 1 ? '' : 's'} not yet screened`, done: false };
  if (individual && !rows.length) return { status: 'partial', detail: 'Name screen clear, the client has not been screened as a person', done: false };
  const resolved = rows.filter(r => r && r.status === 'resolved').length;
  if (resolved) return { status: 'clear', detail: `Clear (${individual ? 'client' : 'company and individuals'}; ${resolved} potential match${resolved === 1 ? '' : 'es'} resolved as not the same person)`, done: true, resolved };
  return { status: 'clear', detail: individual ? 'Clear, no matches (client)' : 'Clear, no matches (company and individuals)', done: true };
}

// Convenience for a text report line, e.g. the audit-record screening evidence.
export function partyScreeningLine(row) {
  const when = row.checkedAt ? new Date(row.checkedAt).toLocaleString('en-GB') : '';
  const label = PARTY_STATUS_LABEL[row.status] || row.status;
  return `${row.name} (${row.role}): ${label}${row.matchCount ? ` · ${row.matchCount} candidate match${row.matchCount === 1 ? '' : 'es'}` : ''} · ${row.source}${when ? ' · ' + when : ''}`;
}
